import { useEffect, useRef, useState } from "react";
import { Box, MenuItem, Popper, Paper, ClickAwayListener, MenuList } from "@mui/material";
import { ChevronDown } from "lucide-react";
import { ActionChip } from "../components/ActionChip";
import { actionPalette, palette } from "../theme";
import { ActionType, Comment } from "../generated/core-types";

interface ValidationActionsProps {
  comment: Comment;
  onChange: (action: ActionType) => void;
}

const OPTIONS: ActionType[] = [
  ActionType.BUY,
  ActionType.REVIEW,
  ActionType.QUESTION,
  ActionType.NOTHING,
  ActionType.CONTEST,
];

export function ValidationActions({ comment, onChange }: ValidationActionsProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<ActionType>(comment.action);
  const anchor = useRef<HTMLButtonElement>(null);

  useEffect(() => setCurrent(comment.action), [comment.action]);

  const handleSelect = (action: ActionType) => {
    setCurrent(action);
    setOpen(false);
    onChange(action);
  };

  return (
    <>
      <Box
        component="button"
        ref={anchor}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "4px 8px 4px 6px",
          borderRadius: "999px",
          backgroundColor: "transparent",
          border: `1px solid ${palette.border}`,
          cursor: "pointer",
          color: palette.ink,
          transition: "border-color 120ms ease",
          "&:hover": { borderColor: palette.ink },
        }}
      >
        <ActionChip action={current} size="sm" />
        <ChevronDown
          size={14}
          strokeWidth={1.8}
          style={{
            color: palette.inkSoft,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 160ms ease",
          }}
        />
      </Box>
      <Popper
        open={open}
        anchorEl={anchor.current}
        placement="bottom-start"
        sx={{ zIndex: 50 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={0}
            sx={{
              mt: 0.5,
              minWidth: 180,
              border: `1px solid ${palette.border}`,
              borderRadius: 10,
              overflow: "hidden",
              boxShadow: "0 12px 32px -16px rgba(26,26,26,0.25)",
            }}
          >
            <MenuList dense disablePadding role="listbox">
              {OPTIONS.map((opt) => {
                const tone = actionPalette[opt];
                const active = opt === current;
                return (
                  <MenuItem
                    key={opt}
                    selected={active}
                    onClick={() => handleSelect(opt)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      py: 1,
                      px: 1.5,
                      fontFamily: '"Geist", system-ui, sans-serif',
                      fontSize: "0.88rem",
                      "&:hover": { backgroundColor: tone.bg },
                      "&.Mui-selected": { backgroundColor: tone.bg },
                      "&.Mui-selected:hover": { backgroundColor: tone.bg },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        backgroundColor: tone.fg,
                        flexShrink: 0,
                      }}
                    />
                    <Box component="span" sx={{ color: palette.ink }}>
                      {tone.label}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        ml: "auto",
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: "0.7rem",
                        color: palette.inkSoft,
                        letterSpacing: "0.06em",
                      }}
                    >
                      {opt}
                    </Box>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
